import { useState, useRef } from 'react';

interface FormValues {
  [key: string]: string;
}

interface FormErrors {
  [key: string]: string;
}

interface FormTouched {
  [key: string]: boolean;
}

type ValidationRule = (value: string, values?: FormValues) => string;

interface ValidationRules {
  [key: string]: ValidationRule;
}

interface FieldProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  error: string | null;
  isTouched: boolean;
  hasError: boolean;
}

interface UseFormValidationReturn {
  values: FormValues;
  errors: FormErrors;
  touched: FormTouched;
  isValid: boolean;
  handleChange: (field: string, value: string) => void;
  handleBlur: (field: string) => void;
  validateAll: () => boolean;
  resetForm: () => void;
  setError: (field: string, message: string) => void;
  clearError: (field: string) => void;
  getFieldProps: (field: string) => FieldProps;
  setValues: React.Dispatch<React.SetStateAction<FormValues>>;
}

// Custom hook for form validation and error handling
export const useFormValidation = (
  initialValues: FormValues,
  validationRules: ValidationRules
): UseFormValidationReturn => {
  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<FormTouched>({});
  const [isValid, setIsValid] = useState<boolean>(false);
  const rulesRef = useRef(validationRules);

  // Handle input change - no validation, just update values
  const handleChange = (field: string, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  // Handle input blur - no validation, just mark as touched
  const handleBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  // Validate all fields - only called on button click
  const validateAll = (): boolean => {
    const newErrors: FormErrors = {};
    let hasErrors = false;

    Object.keys(rulesRef.current).forEach((field) => {
      const value = values[field];
      const rule = rulesRef.current[field];
      const error = rule(value, values);

      if (error) {
        newErrors[field] = error;
        hasErrors = true;
      }

      // Mark all fields as touched
      setTouched((prev) => ({ ...prev, [field]: true }));
    });

    setErrors(newErrors);
    setIsValid(!hasErrors);
    return !hasErrors;
  };

  // Reset form
  const resetForm = () => {
    setValues(initialValues);
    setErrors({});
    setTouched({});
    setIsValid(false);
  };

  // Set specific error
  const setError = (field: string, message: string) => {
    setErrors((prev) => ({ ...prev, [field]: message }));
    setIsValid(false);
  };

  // Clear specific error
  const clearError = (field: string) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    // Recalculate validity
    setIsValid(() => {
      const remaining = Object.keys(errors).filter((key) => key !== field);
      return remaining.length === 0;
    });
  };

  // Get field props for input elements
  const getFieldProps = (field: string): FieldProps => ({
    value: values[field] || '',
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange(field, e.target.value),
    onBlur: () => handleBlur(field),
    error: touched[field] ? errors[field] || null : null,
    isTouched: !!touched[field],
    hasError: !!(touched[field] && errors[field]),
  });

  return {
    values,
    errors,
    touched,
    isValid,
    handleChange,
    handleBlur,
    validateAll,
    resetForm,
    setError,
    clearError,
    getFieldProps,
    setValues,
  };
};

// Common validation rules
export const validationRules = {
  email: (value: string): string => {
    if (!value) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return '';
  },

  password: (value: string): string => {
    if (!value) return 'Password is required';
    if (value.length < 6) return 'Password must be at least 6 characters';
    return '';
  },

  required: (value: string, fieldName: string = 'This field'): string => {
    if (!value || value.trim() === '') return `${fieldName} is required`;
    return '';
  },

  minLength:
    (min: number) =>
    (value: string, fieldName: string = 'This field'): string => {
      if (!value) return `${fieldName} is required`;
      if (value.length < min) return `${fieldName} must be at least ${min} characters`;
      return '';
    },

  maxLength:
    (max: number) =>
    (value: string, fieldName: string = 'This field'): string => {
      if (!value) return '';
      if (value.length > max) return `${fieldName} must be no more than ${max} characters`;
      return '';
    },
};
